<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Menu from 'primevue/menu';
import Tag from 'primevue/tag';
import { useActivities, useDateFns, useResponsive, useUtilities } from '@apparatix/composables';
import { Activity, ActivityTypeName, type ActivityMethodName } from '@apparatix/schemas/Activities';
import ActivityItemIcon from './ActivityItemIcon.vue';
import {
  Section,
  SplitBar,
  Stack,
  TitleBar,
  Toolbar,
  Typography,
} from '@apparatix/library/core';
import { Noir } from '@apparatix/theme/Noir';
import { MenuItem } from 'primevue/menuitem';
import NoteViewer from './NoteViewer.vue';
import RadioFor from '@apparatix/library/RadioFor.vue';
import GotoRelationshipButton from '../GotoRelationshipButton.vue';
import { ActivityItemProps } from './commonProps';
import FileViewerButton from './FileViewerButton.vue';

type Emits = {
  (e: 'completed-change', completed: boolean, activity: Activity): void;
  (e: 'edit', activity: Activity): void;
  (e: 'delete', activityId: number, series: boolean): void;
  (e: 'defer', activity: Activity): void;
}

const props = defineProps<ActivityItemProps>();
const emit = defineEmits<Emits>();

const {
  isPartOfSeries,
  isActivity,
  isNote,
  isReminder,
} = useActivities();
const { formatDate } = useDateFns();
const { arrayHasValues, asKeyedItems, combineArrays } = useUtilities();
const { isMobile } = useResponsive();

const itemMenu = ref();

const menuItems = ref<MenuItem[]>([
  {
    label: props.activity?.completedAt ? 'Mark as incomplete' : 'Mark as complete',
    icon: 'pi pi-check',
    command: () => emit('completed-change', !props.activity?.completedAt, props.activity),
  },
  { separator: true },
  {
    label: 'Edit',
    icon: 'pi pi-pen-to-square',
    command: () => emit('edit', props.activity),
    disabled: !props.activity?.id,
  },
  {
    label: 'Delete',
    icon: 'pi pi-trash',
    disabled: !props.activity?.id,
    command: () => emit('delete', props.activity.id, false),
  },
]);

const isCompleted = computed({
  get() {
    return Boolean(props.activity?.completedAt);
  },
  set(completed) {
    emit('completed-change', completed, props.activity);
  },
});

const relationships = computed(() => {
  const all = combineArrays(props.activity?.accounts, props.activity?.campaigns, props.activity?.contacts);

  return asKeyedItems(
    props.showAllRelationships
      ? all
      : all.filter(relationship => relationship.id !== props.parentId),
  );
});

const onMenuButtonClick = (e: MouseEvent) => itemMenu.value.toggle(e);

onMounted(() => {
  if (props.activity?.type?.name === 'Reminder') {
    const defer: MenuItem = {
      label: 'Defer',
      icon: 'pi pi-stopwatch',
      command: () => emit('defer', props.activity),
    };

    menuItems.value.splice(1, 0, defer);
  }

  if (isPartOfSeries(props.activity)) {
    menuItems.value.push({
      label: 'Delete series',
      icon: 'pi pi-trash',
      command: () => emit('delete', props.activity.id, true),
    });
  }
});
</script>
<template>
  <div class="flex flex-col lg:flex-row gap-4" :data-activity-id="props.activity.id">
    <ActivityItemIcon v-if="isActivity(props.activity)" :method="(props.activity.method?.name as ActivityMethodName)" />
    <ActivityItemIcon v-if="isNote(props.activity) || isReminder(props.activity)" :type="(props.activity.type.name as ActivityTypeName)" />
    <Section>
      <template #title>
        <TitleBar class="pb-4 lg:pb-6">
          <template #title>
            <Typography v-if="isActivity(props.activity)" variant="section-heading" truncate>
              {{ props.activity.title ?? 'Untitled Activity' }}
            </Typography>
            <Typography v-if="isNote(props.activity)" variant="section-heading">
              Note
            </Typography>
            <Typography v-if="isReminder(props.activity)" variant="section-heading">
              Reminder
            </Typography>
            <Tag
              v-if="props.activity?.purpose && !isMobile"
              severity="secondary"
              style="text-wrap: nowrap;"
              :dt="Noir.tag"
              :value="props.activity.purpose.displayName"
            />
          </template>
          <template #button>
            <Button
              icon="pi pi-ellipsis-h"
              variant="outlined"
              :dt="Noir.button"
              rounded
              @click="onMenuButtonClick"
            />
            <Menu ref="itemMenu" :model="menuItems" popup />
          </template>
        </TitleBar>
      </template>
      <Stack>
        <div class="flex items-center justify-between">
          <Typography variant="section-subheading">
            {{ formatDate(props.activity.startDateTime, 'MMMM do, yyyy', '') }}
          </Typography>
          <FileViewerButton v-if="arrayHasValues(props.activity?.files)" :files="props.activity.files" />
        </div>
        <NoteViewer v-if="arrayHasValues(props.activity.notes)" :note-html="props.activity.notes[0].note" />
        <div class="flex gap-2 flex-wrap">
          <GotoRelationshipButton
            v-for="relationship in relationships"
            :key="relationship.key"
            :relationship="relationship"
            :keep-current-parent="props.isInFlyout"
          />
        </div>
      </Stack>
      <template #footer>
        <SplitBar>
          <template #left>
            <Typography variant="section-footer" muted>
              Created by {{ props.activity.createdBy.fullName }} on {{ formatDate(props.activity.createdAt, 'MMMM do, yyyy') }}
            </Typography>
          </template>
          <template #right>
            <div class="hidden lg:flex">
              <Toolbar align="right">
                <RadioFor label="Mark as complete">
                  <template #default="{id}">
                    <Checkbox v-model="isCompleted" :input-id="id" binary />
                  </template>
                </RadioFor>
              </Toolbar>
            </div>
          </template>
        </SplitBar>
      </template>
    </Section>
  </div>
</template>
